/* eslint-disable react-hooks/exhaustive-deps */
import styles from "./Survey.module.css";
import { useEffect, useRef, useState } from "react";
import Button from "../../components/Button";
import Footer from "../../components/Footer";
import surveysService from "../../services/surveys";
import { useParams } from "react-router-dom";
import { ISurveyQuestion } from "../../libs/interfaces/services";
import { SurveyQuestions } from "../SurveyQuestions";
import { Header } from "../../components/Header";
import { useContextSurveyQuiz } from "../../hooks/useSurveyQuiz";

import gifOpen from "../../assets/gif_open_2.gif";
import { Loading } from "../../components/Loading";
import useAssistantStore, { AssistantStore } from "../../store/assistantStore";
import { linkifyHtml } from "../../helpers/linkifyHtml";
import { isValidAnswer } from "../../helpers/isValidAnswer";

function SurveyTopicHome() {
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [surveyTopic, setSurveyTopic] = useState<ISurveyQuestion>();
  const [previousTopic, setPreviousTopic] = useState<{ idTopic: number; title: string } | null>(null);

  const { hashID, surveyID, surveyTopicID } = params;

  const {
    showSurveyQuiz,
    setShowSurveyQuiz,
    surveyQuizIndex,
    setNumTotalQuestions,
    handleAttAnswerList,
    answerList,
    setNumberTopics,
    setSurveyQuizIndex,
    setTotalSurveyQuestions,
    setTotalSurveyAnswered,
    setSurveySubtitle,
    setSurveyTitle,
  } = useContextSurveyQuiz();

  const setHasAssistant = useAssistantStore((state: AssistantStore) => state.setHasAssistant);
  const surveyContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadSurveyQuestion() {
      if (params.surveyID) {
        const response = await surveysService.listSurveyQuestions(
          params.surveyID,
          params.hashID ? params.hashID : ""
        );

        setHasAssistant(response.object.hasAssistant);

        await handleAttAnswerList(
          params.surveyID,
          params.hashID,
          surveyTopicID
        );

        setSurveyTopic(
          response.object.topics.find(
            (topic) => topic.idTopic === Number(surveyTopicID)
          )
        );
        setLoading(false);
      }
    }
    loadSurveyQuestion();
  }, []);

  useEffect(() => {
    async function loadSurveyTopics() {
      const response = await surveysService.listSurveyQuestions(
        String(surveyID),
        hashID ? hashID : ""
      );

      setHasAssistant(response.object.hasAssistant);

      const topics = response.object.topics;
      setNumberTopics(
        topics.map((topic) => ({ idTopic: Number(topic.idTopic) }))
      );

      // Identifica o tópico anterior
      const currentIdx = topics.findIndex(
        (t) => Number(t.idTopic) === Number(surveyTopicID)
      );
      if (currentIdx > 0) {
        const prev = topics[currentIdx - 1];
        setPreviousTopic({ idTopic: Number(prev.idTopic), title: prev.title });
      } else {
        setPreviousTopic(null);
      }

      // Calcula o progresso geral da pesquisa
      await computeOverallProgress(topics);
    }
    loadSurveyTopics();
  }, []);

  // Carrega o nome do questionário (survey) para exibir como referência
  useEffect(() => {
    async function loadSurveyName() {
      if (hashID && surveyID) {
        const response = await surveysService.listSurveys(hashID);
        const surveyList = response?.object?.surveyList;
        if (surveyList) {
          const currentSurvey = surveyList.find(
            (item) => Number(item.idSurvey) === Number(surveyID)
          );
          if (currentSurvey?.subtitle) {
            setSurveySubtitle(currentSurvey.subtitle);
          }
          if (currentSurvey?.title) {
            setSurveyTitle(currentSurvey.title as string);
          }
        }
      }
    }
    loadSurveyName();
  }, []);

  // Calcula o total geral de questões e respostas da pesquisa inteira
  const computeOverallProgress = async (topics: ISurveyQuestion[]) => {
    // Total de questões = soma de todas as questions de todos os tópicos
    const totalQuestions = topics.reduce(
      (sum, topic) => sum + topic.questions.length,
      0
    );
    setTotalSurveyQuestions(totalQuestions);

    // Busca todas as respostas da pesquisa
    const allAnswersResp = await surveysService.listSurveyAnswer(
      String(surveyID),
      hashID || ""
    );
    const allAnswers = allAnswersResp.object || [];

    // Conta quantas questões (idQuestion) possuem pelo menos uma resposta válida
    const answeredQuestionIds = new Set<number>();
    for (const answer of allAnswers) {
      if (isValidAnswer(answer)) {
        answeredQuestionIds.add(answer.idQuestion);
      }
    }
    setTotalSurveyAnswered(answeredQuestionIds.size);
  };

  useEffect(() => {
    if (surveyTopic && answerList && answerList?.length > 0) {
      const quest = surveyTopic?.questions.map(
        (question) => question?.idQuestion
      );

      // Filtra apenas respostas válidas (não vazias)
      const validAnswers = answerList.filter(isValidAnswer);
      const answers = validAnswers.map((answer) => answer.idQuestion);

      const notAnswer = quest?.filter(
        (question) => !answers.includes(question)
      );

      const firstNotAnswer = quest?.indexOf(notAnswer[0]);

      if (answers.length >= quest.length) {
        setSurveyQuizIndex(0);
      }

      if (notAnswer.length > 0) {
        setSurveyQuizIndex(firstNotAnswer);
      }
    }
  }, [surveyTopic]);

  function handleChangeStartQuiz() {
    setShowSurveyQuiz(true);
    setNumTotalQuestions(
      surveyTopic?.questions ? surveyTopic?.questions.length : 0
    );
    window.scrollTo(0, 0);
  }

  function handleBack() {
    window.location.href = `/${params.hashID}`;
  }

  const isAssistantActive = useAssistantStore((state: AssistantStore) => state.isActive);

  // Quando a capa do tópico é exibida, faz scroll para baixo até o conteúdo
  useEffect(() => {
    if (!loading && !showSurveyQuiz && surveyContentRef.current) {
      surveyContentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [loading, showSurveyQuiz]);

  return (
    <>
      <div className={`${styles.survey} ${isAssistantActive ? styles.surveyWithAssistant : ""}`}>
        {loading ? (
          <Loading message="Carregando questões" subtitle="Buscando suas respostas anteriores" />
        ) : !showSurveyQuiz ? (
          <>
            <div className={styles.box}>
              <Header handleNavigate={() => handleBack()} />
              <div className={styles.surveyIconContainer}>
                <img src={gifOpen} alt="gif open" className={styles.gifOpen} />
              </div>
            </div>

            <div ref={surveyContentRef} className={styles.surveyContent}>
              {previousTopic && (
                <div
                  className={styles.previousTopicLabel}
                  onClick={() => {
                    window.location.href = `/${hashID}/survey-topic/${surveyID}/${previousTopic.idTopic}`;
                  }}
                >
                  &#8592; Tópico anterior
                </div>
              )}

              <div className={styles.surveyContentText}>
                <h1>{surveyTopic?.title}</h1>
                <h2 style={{ textAlign: "left" }}>{surveyTopic?.subtitle}</h2>
                <div
                  className={styles.topicDescription}
                  style={{ textAlign: "left" }}
                  dangerouslySetInnerHTML={{
                    __html: surveyTopic?.description
                      ? linkifyHtml(surveyTopic?.description)
                      : "",
                  }}
                />
              </div>

              <div className={styles.surveyContentFooter}>
                <Button
                  handleAction={() => handleChangeStartQuiz()}
                  text="Iniciar"
                  type="lightBlue"
                  width="100%"
                />
              </div>
            </div>
          </>
        ) : (
          <SurveyQuestions
            surveyTopic={surveyTopic ? surveyTopic : ({} as ISurveyQuestion)}
            indexQuiz={surveyQuizIndex}
          />
        )}
      </div>
    </>
  );
}

export default SurveyTopicHome;
